/**
 * VoiceServiceModule — wraps vibe-designer's /api/voice/session WS.
 *
 * M3a scope: open/close the WebSocket + dispatch transcript frames to
 * subscribers. The actual STT protocol (whisperx config frames, audio
 * binary chunks) is owned by the existing vibe-designer VoicePanel +
 * voiceTurnSocket.ts and not re-implemented here. The Briefing Room
 * module subscribes to transcripts to drive A2+ animations (voice-
 * triggered die rolls); audio capture stays in vibe-designer's chrome.
 *
 * Reconnect: exponential backoff with jitter, cap 30s, factor 2,
 * initial 1s (planner-track M3 risk #3 mitigation).
 */
import {
  SttUnavailableError,
  TtsUnavailableError,
  type SpeakOptions,
  type SpeakResult,
  type SttOptions,
  type SttSession,
  type TranscriptListener,
  type UnsubscribeFn,
  type VoiceService,
} from '../types';
import { getAuthHeaders, getBearerToken } from './authHeaders';

/** D1-1 (2026-06-09 audit) — RFC 6455 §7.4.1 policy-violation, sent by the
 *  backend voice WS auth gate when the first auth frame is missing/invalid.
 *  The session reconnect loop treats this as terminal (do NOT retry a bad
 *  token every 30s). */
const WS_CLOSE_POLICY_VIOLATION = 1008;

/** D1-1 — the mandatory first WS frame carrying the JWT (browsers can't set
 *  WS Authorization headers; a `?token=` query param would land in nginx
 *  access logs, so it travels in the frame body). No-op when no token. */
function sendVoiceAuthFrame(ws: WebSocket): void {
  const token = getBearerToken();
  if (token) {
    ws.send(JSON.stringify({ type: 'auth', token }));
  }
}

const SESSION_PATH = '/api/voice/session';
const TURN_PATH = '/api/voice/turn';
const TTS_PATH = '/api/voice/tts';
const DEFAULT_TTS_VOICE = 'bm_lewis';
const DEFAULT_STT_MIME = 'audio/webm;codecs=opus';
const STT_TIMEOUT_MS = 30_000;
const INITIAL_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30_000;
const BACKOFF_FACTOR = 2;

interface TranscriptFrame {
  type: 'transcript';
  text: string;
}

function isTranscriptFrame(value: unknown): value is TranscriptFrame {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const f = value as Record<string, unknown>;
  return f.type === 'transcript' && typeof f.text === 'string';
}

export class VoiceServiceModule implements VoiceService {
  private socket: WebSocket | null = null;
  private listeners = new Set<TranscriptListener>();
  private backoffMs = INITIAL_BACKOFF_MS;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private explicitlyStopped = false;

  constructor(
    private readonly sessionPath: string = SESSION_PATH,
    private readonly ttsPath: string = TTS_PATH,
    private readonly turnPath: string = TURN_PATH,
  ) {}

  isAvailable(): boolean {
    return typeof window !== 'undefined' && 'WebSocket' in window;
  }

  // V1 Voice GM (2026-06-02): TTS narration via vibe-designer's
  // POST /api/voice/tts (routes/voice.py:1129-1219). Returns a blob URL +
  // revoke callback. The route synthesizes WAV bytes server-side via
  // Kokoro (default) or qwen3-tts; we just play the blob. Mid-fetch
  // cancellation via opts.signal — callers (ReadAloudControl) drive an
  // AbortController so navigating to a new paragraph stops the fetch.
  // V2 Voice GM (2026-06-02) — STT capture session.
  //
  // Client flow per routes/voice.py:603-865 (v19.2):
  //   1. getUserMedia + MediaRecorder
  //   2. open WS (/api/voice/turn — auth middleware skips WS upgrades per
  //      middleware/auth.py:185-186)
  //   3. on WS open: send {type:'config', engine?, language, audio_mime_type, turn_id}
  //   4. recorder.ondataavailable → ws.send(arrayBuffer)
  //   5. session.stop() → recorder.stop() → wait for trailing chunk
  //      → ws.send({type:'stop'}) → wait for {type:'transcript'} → resolve
  //   6. session.cancel() → recorder.stop() + ws.close() without 'stop'
  async startStt(opts?: SttOptions): Promise<SttSession> {
    if (
      typeof window === 'undefined' ||
      !('WebSocket' in window) ||
      !navigator?.mediaDevices?.getUserMedia ||
      typeof MediaRecorder === 'undefined'
    ) {
      throw new SttUnavailableError('browser missing MediaRecorder / getUserMedia / WebSocket');
    }
    const mimeType = opts?.mimeType ?? DEFAULT_STT_MIME;
    if (!MediaRecorder.isTypeSupported(mimeType)) {
      throw new SttUnavailableError(`unsupported mimeType: ${mimeType}`);
    }

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err: unknown) {
      throw new SttUnavailableError(
        err instanceof Error ? err.message : 'mic permission denied',
      );
    }

    const recorder = new MediaRecorder(stream, { mimeType });
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = `${proto}//${window.location.host}${this.turnPath}`;
    const ws = new WebSocket(url);
    ws.binaryType = 'arraybuffer';

    const partialListeners = new Set<(text: string) => void>();
    const finalListeners = new Set<(text: string) => void>();
    const errorListeners = new Set<(err: Error) => void>();

    const cleanup = () => {
      try {
        if (recorder.state !== 'inactive') {
          recorder.stop();
        }
      } catch {
        // noop
      }
      stream.getTracks().forEach((t) => t.stop());
      try {
        ws.close();
      } catch {
        // noop
      }
    };

    const emitError = (err: Error) => {
      for (const cb of errorListeners) {
        try {
          cb(err);
        } catch {
          // listener throw must not block downstream
        }
      }
    };

    let finalTranscript: string | null = null;
    let finalResolve: ((text: string) => void) | null = null;
    let finalReject: ((err: Error) => void) | null = null;

    ws.onmessage = (event) => {
      if (typeof event.data !== 'string') {
        return;
      }
      try {
        const frame = JSON.parse(event.data) as Record<string, unknown>;
        if (frame.type === 'transcript' && typeof frame.text === 'string') {
          finalTranscript = frame.text;
          for (const cb of finalListeners) {
            try {
              cb(frame.text);
            } catch {
              // noop
            }
          }
          if (finalResolve !== null) {
            finalResolve(frame.text);
            finalResolve = null;
            finalReject = null;
          }
          cleanup();
        } else if (frame.type === 'error') {
          const message =
            typeof frame.message === 'string' ? frame.message : 'stt error';
          const err = new SttUnavailableError(message);
          emitError(err);
          if (finalReject !== null) {
            finalReject(err);
            finalResolve = null;
            finalReject = null;
          }
          cleanup();
        }
      } catch {
        // Non-JSON frames silently dropped (matches session WS behavior).
      }
    };
    ws.onerror = () => {
      const err = new SttUnavailableError('websocket error');
      emitError(err);
      if (finalReject !== null) {
        finalReject(err);
      }
    };
    ws.onclose = () => {
      if (finalReject !== null && finalTranscript === null) {
        finalReject(new SttUnavailableError('websocket closed before transcript'));
        finalResolve = null;
        finalReject = null;
      }
      stream.getTracks().forEach((t) => t.stop());
    };

    // Buffer audio chunks until WS is OPEN; flush on open.
    const audioBuffer: ArrayBuffer[] = [];
    let wsOpen = false;
    recorder.ondataavailable = (event) => {
      try {
        const blob = event.data;
        if (blob.size === 0) {
          return;
        }
        if (typeof blob.arrayBuffer !== 'function') {
          // JSDOM Blob lacks arrayBuffer (vitest tests stubbing
          // MediaRecorder hit this path). Real browsers always have it.
          return;
        }
        void blob
          .arrayBuffer()
          .then((buf) => {
            if (wsOpen && ws.readyState === WebSocket.OPEN) {
              ws.send(buf);
            } else {
              audioBuffer.push(buf);
            }
          })
          .catch(() => {
            // Audio chunk read failed; drop silently — the user will see
            // "no_audio" from the server if every chunk fails to read.
          });
      } catch {
        // Belt-and-braces against any synchronous throw inside the
        // handler — listener-throws must not block the recorder.
      }
    };

    await new Promise<void>((resolve, reject) => {
      const openTimer = setTimeout(() => {
        reject(new SttUnavailableError('websocket open timeout'));
      }, 10_000);
      ws.onopen = () => {
        clearTimeout(openTimer);
        wsOpen = true;
        // D1-1 — auth frame FIRST, before config.
        sendVoiceAuthFrame(ws);
        ws.send(
          JSON.stringify({
            type: 'config',
            engine: opts?.engine,
            language: opts?.language ?? 'auto',
            audio_mime_type: mimeType,
          }),
        );
        for (const chunk of audioBuffer) {
          ws.send(chunk);
        }
        audioBuffer.length = 0;
        recorder.start(250);
        resolve();
      };
    });

    return {
      onPartial(cb) {
        partialListeners.add(cb);
        return () => partialListeners.delete(cb) as unknown as void;
      },
      onFinal(cb) {
        finalListeners.add(cb);
        return () => finalListeners.delete(cb) as unknown as void;
      },
      onError(cb) {
        errorListeners.add(cb);
        return () => errorListeners.delete(cb) as unknown as void;
      },
      async stop(): Promise<string> {
        if (finalTranscript !== null) {
          return finalTranscript;
        }
        return new Promise<string>((resolve, reject) => {
          finalResolve = resolve;
          finalReject = reject;
          const timer = setTimeout(() => {
            if (finalReject !== null) {
              finalReject(new SttUnavailableError('stt timeout'));
              finalResolve = null;
              finalReject = null;
            }
            cleanup();
          }, STT_TIMEOUT_MS);
          const wrap = (orig: typeof resolve) => (text: string) => {
            clearTimeout(timer);
            orig(text);
          };
          finalResolve = wrap(resolve);
          try {
            if (recorder.state !== 'inactive') {
              recorder.requestData();
              recorder.stop();
            }
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: 'stop' }));
            }
          } catch (err: unknown) {
            clearTimeout(timer);
            reject(
              err instanceof Error
                ? err
                : new SttUnavailableError(String(err)),
            );
          }
        });
      },
      cancel(): void {
        finalResolve = null;
        finalReject = null;
        cleanup();
      },
    };
  }

  async speak(text: string, opts?: SpeakOptions): Promise<SpeakResult> {
    const trimmed = (text ?? '').trim();
    if (trimmed.length === 0) {
      throw new TtsUnavailableError('empty text');
    }
    const body: Record<string, unknown> = {
      text: trimmed,
      voice: opts?.voice ?? DEFAULT_TTS_VOICE,
    };
    if (opts?.engine) {
      body.engine = opts.engine;
    }
    let response: Response;
    try {
      response = await fetch(this.ttsPath, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(body),
        signal: opts?.signal,
      });
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        throw err;
      }
      throw new TtsUnavailableError(
        err instanceof Error ? err.message : String(err),
      );
    }
    if (!response.ok) {
      throw new TtsUnavailableError(`HTTP ${response.status}`);
    }
    const blob = await response.blob();
    const audioUrl = URL.createObjectURL(blob);
    return {
      audioUrl,
      revoke: () => URL.revokeObjectURL(audioUrl),
    };
  }

  async startListening(): Promise<void> {
    if (!this.isAvailable()) {
      return;
    }
    this.explicitlyStopped = false;
    if (this.socket !== null && this.socket.readyState === WebSocket.OPEN) {
      return;
    }
    this.openSocket();
  }

  async stopListening(): Promise<void> {
    this.explicitlyStopped = true;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.socket !== null) {
      this.socket.close();
      this.socket = null;
    }
  }

  onTranscript(cb: TranscriptListener): UnsubscribeFn {
    this.listeners.add(cb);
    return () => {
      this.listeners.delete(cb);
    };
  }

  private openSocket(): void {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = `${proto}//${window.location.host}${this.sessionPath}`;
    const ws = new WebSocket(url);
    this.socket = ws;
    ws.onopen = () => {
      // D1-1 — auth frame FIRST. This relay socket sends no config of its
      // own, but the backend gate still requires the auth frame before it
      // will keep the connection past ws.accept().
      sendVoiceAuthFrame(ws);
      this.backoffMs = INITIAL_BACKOFF_MS;
    };
    ws.onmessage = (event) => {
      try {
        const frame = JSON.parse(event.data) as unknown;
        if (isTranscriptFrame(frame)) {
          this.dispatch(frame.text);
        }
      } catch {
        // Non-JSON frames are silently dropped — the existing voice
        // pipeline also emits binary audio chunks that aren't ours.
      }
    };
    ws.onclose = (ev) => {
      if (this.socket === ws) {
        this.socket = null;
      }
      // D1-1 (G0.W1) — a 1008 close means the auth frame was rejected
      // (missing/expired/invalid token). Do NOT reconnect: retrying the same
      // bad token every 30s is a pointless loop. The SPA's auth layer
      // (/api/me 401 → redirect) handles re-authentication. Any other close
      // code keeps the existing reconnect-with-backoff behavior.
      if (ev.code === WS_CLOSE_POLICY_VIOLATION) {
        this.explicitlyStopped = true;
      }
      if (!this.explicitlyStopped) {
        this.scheduleReconnect();
      }
    };
    ws.onerror = () => {
      ws.close();
    };
  }

  private dispatch(text: string): void {
    for (const cb of this.listeners) {
      try {
        cb(text);
      } catch {
        // A listener throwing must not block downstream listeners.
      }
    }
  }

  private scheduleReconnect(): void {
    const jitter = Math.random() * 0.3 * this.backoffMs;
    const delay = Math.min(this.backoffMs + jitter, MAX_BACKOFF_MS);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.openSocket();
    }, delay);
    this.backoffMs = Math.min(this.backoffMs * BACKOFF_FACTOR, MAX_BACKOFF_MS);
  }
}
